import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ImagePlus, Trash2, UploadCloud, X } from 'lucide-react';
import Button from '../../../components/commoncomponents/Button';
import { resolveApiAssetUrl } from '../../../services/api';

const MAX_LOGO_SIZE = 320;
const LOGO_QUALITY = 0.86;

function resizeLogoFile(file) {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      reject(new Error('Please choose a valid image file.'));
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      const image = new Image();

      image.onload = () => {
        const scale = Math.min(1, MAX_LOGO_SIZE / Math.max(image.width, image.height));
        const width = Math.max(1, Math.round(image.width * scale));
        const height = Math.max(1, Math.round(image.height * scale));
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        canvas.width = width;
        canvas.height = height;
        context.drawImage(image, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Unable to prepare that image file.'));
              return;
            }

            resolve({
              file: new File([blob], 'business-logo.jpg', { type: 'image/jpeg' }),
              previewUrl: canvas.toDataURL('image/jpeg', LOGO_QUALITY),
            });
          },
          'image/jpeg',
          LOGO_QUALITY
        );
      };

      image.onerror = () => reject(new Error('Unable to read that image file.'));
      image.src = reader.result;
    };

    reader.onerror = () => reject(new Error('Unable to read that image file.'));
    reader.readAsDataURL(file);
  });
}

function BusinessBrandLogoModal({ business, isOpen, onClose, onSave, saving, error }) {
  const [logoPreview, setLogoPreview] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [fileError, setFileError] = useState('');

  useEffect(() => {
    if (isOpen && business) {
      setLogoPreview(resolveApiAssetUrl(business.logo));
      setLogoFile(null);
      setFileError('');
    }
  }, [business, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const { body } = document;
    const previousOverflow = body.style.overflow;

    body.style.overflow = 'hidden';

    return () => {
      body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!isOpen || !business) {
    return null;
  }

  function handleSubmit(event) {
    event.preventDefault();
    onSave({
      file: logoFile,
      removeLogo: !logoPreview,
    });
  }

  async function handleFileChange(event) {
    const [file] = event.target.files;

    if (!file) {
      return;
    }

    setFileError('');

    try {
      const resizedLogo = await resizeLogoFile(file);
      setLogoFile(resizedLogo.file);
      setLogoPreview(resizedLogo.previewUrl);
    } catch (uploadError) {
      setFileError(uploadError.message || 'Unable to use that image file.');
    } finally {
      event.target.value = '';
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-[rgba(17,17,17,0.26)] px-4 py-6 backdrop-blur-[4px]">
      <div className="max-h-[calc(100dvh-3rem)] w-full max-w-[520px] overflow-y-auto rounded-[28px] border border-[#e9e0d8] bg-[linear-gradient(180deg,#fdfcfb_0%,#faf6f1_100%)] shadow-[0_24px_60px_rgba(17,17,17,0.14)]">
        <div className="border-b border-[#ebe3db] px-5 py-5 sm:px-6">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-4">
                <span className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-[18px] border border-[#e7ddd3] bg-[#f7f4ef] text-[#6f5644]">
                  <ImagePlus className="h-[22px] w-[22px]" />
                </span>
                <div className="min-w-0">
                  <h3 className="m-0 text-[1.35rem] font-semibold tracking-[-0.03em] text-[#111111]">
                    Change Business Brand/Logo
                  </h3>
                  <p className="mt-1 text-sm leading-5 text-[#7f736b]">
                    Update the logo shown for <span className="font-semibold text-[#111111]">{business.name}</span>.
                  </p>
                </div>
              </div>
            </div>
            <button
              aria-label="Close brand logo modal"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#e5dfd8] bg-white text-[#7f736b] shadow-[0_10px_24px_rgba(17,17,17,0.05)] transition hover:bg-[#faf7f3]"
              type="button"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <form className="grid gap-4 px-5 py-5 sm:px-6" onSubmit={handleSubmit}>
          <label className="grid cursor-pointer gap-3 rounded-[20px] border border-dashed border-[#d8cec4] bg-white px-4 py-5 text-center transition hover:border-[#bca996] hover:bg-[#faf7f3]">
            <input className="sr-only" type="file" accept="image/*" onChange={handleFileChange} />
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-[18px] border border-[#e7ddd3] bg-[#f7f4ef] text-[#6f5644]">
              <UploadCloud className="h-5.5 w-5.5" />
            </span>
            <span>
              <span className="block text-sm font-semibold text-[#111111]">Upload logo image</span>
              <span className="mt-1 block text-sm leading-6 text-[#8b7a6d]">
                Choose an image from this device. It will be resized for the card.
              </span>
            </span>
          </label>

          {logoPreview ? (
            <div className="flex items-center justify-between gap-3 rounded-[20px] border border-[#e6ddd4] bg-white px-4 py-4">
              <div className="flex min-w-0 items-center gap-3">
              <img
                className="h-12 w-12 rounded-[16px] border border-[#e6ddd4] object-cover"
                src={logoPreview}
                alt=""
              />
                <span className="text-sm text-[#8b7a6d]">Logo preview</span>
              </div>
              <button
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#ead8d2] bg-[#fff8f6] text-[#a44d3e] transition hover:bg-[#fff1ed]"
                type="button"
                onClick={() => {
                  setLogoFile(null);
                  setLogoPreview('');
                }}
                aria-label="Remove logo"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ) : null}

          {fileError || error ? (
            <div className="rounded-[18px] border border-[#f0d4cb] bg-[#fff8f6] px-4 py-3 text-sm text-[#b4533f]">
              {fileError || error}
            </div>
          ) : null}

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <Button
              className="w-full justify-center rounded-[16px] px-5 py-3 sm:w-auto"
              variant="secondary"
              type="button"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              className="w-full justify-center rounded-[16px] px-5 py-3 sm:w-auto"
              type="submit"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Logo'}
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

export default BusinessBrandLogoModal;
